import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { consumePendingPlan, setPendingPlan } from "../lib/flowCheckout";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const expired =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("expired") === "1";
  const [error, setError] = useState(
    expired ? "Tu sesión expiró por inactividad. Vuelve a iniciar sesión." : ""
  );
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = location.state?.from || "/chat";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      // If the user came here from a "Suscribirse" button, send them back
      // to the landing page so the gateway selector modal can open. The
      // landing has the safety-net useEffect that consumes the pending plan.
      const { plan, gateway } = consumePendingPlan();
      if (plan) {
        // Re-store so the landing's useEffect can read it (consumePendingPlan
        // wipes the value).
        setPendingPlan(plan, gateway);
        navigate("/", { replace: true });
        return;
      }
      navigate(redirectTo, { replace: true });
    } else {
      setError(res.error || "No se pudo iniciar sesión.");
    }
  };

  return (
    <AuthShell title="Iniciar sesión" subtitle="Accede a tu cuenta de Pre-Legal.cl">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="tu@email.cl"
          autoComplete="email"
          autoFocus
          testId="login-email-input"
        />
        <Field
          label="Contraseña"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          autoComplete="current-password"
          testId="login-password-input"
        />
        {error && (
          <p
            data-testid="login-error"
            className="text-sm rounded-lg px-3 py-2"
            style={{
              color: "#991b1b",
              background: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg font-semibold px-6 py-3 text-sm transition-all hover:-translate-y-0.5 disabled:cursor-wait"
          style={{
            background: "var(--pl-primary)",
            color: "#fff",
            boxShadow: "0 10px 24px rgba(37, 99, 235, 0.30)",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {submitting ? "Entrando…" : "Entrar"}
        </button>
      </form>
      <p className="text-sm text-center mt-6" style={{ color: "var(--pl-text-muted)" }}>
        ¿No tienes cuenta?{" "}
        <Link
          to="/register"
          data-testid="login-go-register"
          style={{ color: "var(--pl-primary)", fontWeight: 600 }}
          className="hover:underline"
        >
          Regístrate gratis
        </Link>
      </p>
    </AuthShell>
  );
};

// ---------------- shared shell + field ----------------

export const AuthShell = ({ title, subtitle, children }) => (
  <div
    className="min-h-screen w-full"
    style={{
      background:
        "radial-gradient(1400px 900px at 65% -10%, rgba(0, 200, 255, 0.22), transparent 60%), " +
        "radial-gradient(900px 700px at 20% 40%, rgba(0, 150, 196, 0.12), transparent 70%), " +
        "linear-gradient(180deg, #0a2236 0%, #0d2d44 8%, #0a1f30 28%, #06121b 65%, #03080d 100%)",
    }}
  >
    <div className="relative z-10 min-h-screen flex flex-col">
      <header className="px-6 py-5">
        <Link to="/" className="inline-flex items-center gap-2.5" data-testid="auth-logo">
          <img
            src="/logo-prelegal.png"
            alt="Pre-Legal.cl"
            width={36}
            height={36}
            style={{ width: 36, height: 36, objectFit: "contain", display: "block" }}
          />
          <span className="text-lg font-bold tracking-tight" style={{ color: "var(--pl-text-strong)" }}>
            Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span>
          </span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div
          className="w-full max-w-md rounded-2xl p-8 sm:p-10"
          style={{
            background: "rgba(15, 25, 40, 0.45)",
            border: "1px solid rgba(0, 200, 255, 0.22)",
            boxShadow: "0 18px 60px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(0, 200, 255, 0.08) inset",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
          }}
        >
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
            style={{ color: "var(--pl-text-strong)", letterSpacing: "-0.02em" }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm mb-7" style={{ color: "var(--pl-text-muted)" }}>
              {subtitle}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  </div>
);

export const Field = ({ label, type, value, onChange, placeholder, autoComplete, autoFocus, testId, helper }) => (
  <label className="block">
    <span className="text-xs font-semibold mb-1.5 inline-block" style={{ color: "var(--pl-text)", letterSpacing: "0.02em" }}>
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      data-testid={testId}
      className="w-full rounded-lg px-4 py-3 outline-none transition-colors text-base sm:text-sm"
      style={{
        background: "#ffffff",
        border: "1px solid #cbd5e1",
        color: "#0f172a",
        caretColor: "var(--pl-primary)",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--pl-primary)";
        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0, 200, 255, 0.18)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "#cbd5e1";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
    {helper && (
      <span className="text-xs mt-1.5 block" style={{ color: "var(--pl-text-muted)" }}>
        {helper}
      </span>
    )}
  </label>
);

export default LoginPage;
